"use client"

import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../services/apiService';
import { DashboardHeader } from '../components/ui/dashboard-header';
import { CustomOverlaySidebar } from '../components/ui/custom-overlay-sidebar';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Activity, Database, Clock, Search, Check, Plus, AlertCircle, Server,
} from 'lucide-react';
import { Sparkline } from '../components/ui/sparkline';
import { MachineHistoryPanel } from '../components/ui/machine-history-panel';
import { useLogout } from '../hooks/useLogout';
import { useSidebar } from '../hooks/useSidebar';
import type { HistoryMachine, HistorySettings, Machine } from '../types';

/**
 * Analytics — stats history settings.
 *
 * History is opt-in per machine: while it is off, only the machine's latest
 * reading is kept (ServiceStats is overwritten on every heartbeat). Turning it
 * on starts saving a snapshot of the fields below on every report, so trends
 * over time can be charted later.
 */

// Plain-English labels for the stored metric names the backend reports.
const METRIC_LABELS: Record<string, string> = {
  cpu_usage: 'CPU usage',
  memory_usage: 'Memory usage',
  disk_usage: 'Disk usage',
  temperature: 'CPU temperature',
  patients_sync_pending: 'Patients pending sync',
  patients_synced: 'Patients synced',
  patients_not_synced: 'Patients not synced',
  images_processed: 'Images processed',
  tasks_pending: 'Tasks pending',
  system_status: 'System status',
};

// Shared so the header row and the data rows cannot drift apart.
const ROW_COLUMNS = '1.8fr 1.2fr 130px 0.8fr 0.8fr 0.7fr 1fr auto';

const prettyMetric = (name: string) =>
  METRIC_LABELS[name] || name.replace(/_/g, ' ');

const relativeTime = (iso: string | null) => {
  if (!iso) return 'No data yet';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

// How long the samples a machine holds actually span, in rough terms.
const coverage = (sampleCount: number, intervalMinutes: number) => {
  if (!sampleCount) return '—';
  const totalMinutes = sampleCount * intervalMinutes;
  if (totalMinutes < 60) return `~${totalMinutes}m`;
  const hours = Math.round(totalMinutes / 60);
  if (hours < 48) return `~${hours}h`;
  return `~${Math.round(hours / 24)}d`;
};

const AnalyticsPage: React.FC = () => {
  const [settings, setSettings] = useState<HistorySettings | null>(null);
  const [allMachines, setAllMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [addSearch, setAddSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [openMachine, setOpenMachine] = useState<HistoryMachine | null>(null);
  // Row sparklines: serial -> recent cpu_usage values (null = gap).
  const [trends, setTrends] = useState<Record<string, (number | null)[]>>({});

  const { sidebarOpen, closeSidebar, toggleSidebar } = useSidebar();
  const handleLogout = useLogout();

  const fetchData = async () => {
    setLoading(true);
    const [settingsRes, machinesRes] = await Promise.all([
      apiService.getHistorySettings(),
      apiService.getMachines(),
    ]);

    if (settingsRes.success) {
      setSettings(settingsRes.data);
      setError(null);
    } else {
      setError(settingsRes.error || 'Failed to load history settings');
    }
    if (machinesRes.success && Array.isArray(machinesRes.data)) {
      setAllMachines(machinesRes.data);
    }
    setLoading(false);

    // Row sparklines load after the table so they never delay first paint.
    if (settingsRes.success) {
      const withData = settingsRes.data.machines.filter((m) => m.sample_count > 0);
      const series = await Promise.all(
        withData.map((m) =>
          apiService
            .getMachineHistory(m.serial_number, { hours: 24, metrics: ['cpu_usage'], points: 40 })
            .then((r) => [m.serial_number, r.success ? r.data.series.cpu_usage : []] as const),
        ),
      );
      setTrends(
        Object.fromEntries(
          series.map(([serial, vals]) => [
            serial,
            (vals || []).map((v) => (typeof v === 'number' ? v : null)),
          ]),
        ),
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const setHistory = async (serialNumber: string, enabled: boolean) => {
    setSaving(serialNumber);
    const res = await apiService.updateMachineHistory(serialNumber, {
      history_enabled: enabled,
    });
    if (res.success) {
      await fetchData();
      setError(null);
    } else {
      setError(res.error || `Could not update ${serialNumber}`);
    }
    setSaving(null);
  };

  // Memoised so the `?? []` fallback does not hand the useMemos below a fresh
  // array on every render, which would defeat their caching entirely.
  const enabled = useMemo(() => settings?.machines ?? [], [settings]);
  const interval = settings?.capture_interval_minutes ?? 15;

  const filteredEnabled = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return enabled;
    return enabled.filter(
      (m) =>
        m.serial_number.toLowerCase().includes(q) ||
        (m.site_name || '').toLowerCase().includes(q),
    );
  }, [enabled, search]);

  // Machines that could be turned on — everything not already recording.
  const candidates = useMemo(() => {
    const on = new Set(enabled.map((m) => m.serial_number));
    const q = addSearch.trim().toLowerCase();
    return allMachines
      .filter((m) => !on.has(m.serial_number))
      // History is for the production fleet; trial machines do not record it.
      .filter((m) => m.activation_status === 'activated')
      .filter(
        (m) =>
          !q ||
          m.serial_number.toLowerCase().includes(q) ||
          (m.site_name || '').toLowerCase().includes(q),
      )
      .slice(0, 12);
  }, [allMachines, enabled, addSearch]);

  const totalSamples = useMemo(
    () => enabled.reduce((sum, m) => sum + m.sample_count, 0),
    [enabled],
  );

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-screen"
        style={{ backgroundColor: '#0f0f0f', color: '#e2e8f0' }}
      >
        <h2>Loading analytics settings...</h2>
      </div>
    );
  }

  const SectionHeader = ({ title }: { title: string }) => (
    <div className="flex items-center gap-3 mb-5">
      <span
        className="text-xs font-bold uppercase tracking-widest"
        style={{ color: 'rgba(255, 255, 255, 0.4)' }}
      >
        {title}
      </span>
      <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)' }} />
    </div>
  );

  const panel: React.CSSProperties = {
    backgroundColor: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  };

  const Toggle = ({
    on, busy, onClick,
  }: { on: boolean; busy: boolean; onClick: () => void }) => (
    <button
      onClick={onClick}
      disabled={busy}
      role="switch"
      aria-checked={on}
      className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50"
      style={{
        backgroundColor: on ? '#0d9488' : 'rgba(255,255,255,0.15)',
        cursor: busy ? 'wait' : 'pointer',
      }}
    >
      <span
        className="inline-block h-5 w-5 rounded-full bg-white transition-transform duration-200"
        style={{ transform: on ? 'translateX(22px)' : 'translateX(2px)', marginTop: 2 }}
      />
    </button>
  );

  return (
    // textAlign resets the global `.App { text-align: center }` from App.css.
    <div style={{ backgroundColor: '#0f0f0f', minHeight: '100vh', textAlign: 'left' }}>
      <CustomOverlaySidebar isOpen={sidebarOpen} onClose={closeSidebar} />

      <div className="w-full min-h-screen">
        <DashboardHeader
          onLogout={handleLogout}
          onSidebarToggle={toggleSidebar}
          title="Analytics"
          showFilters={false}
        />

        <div className="p-6 space-y-10">
          {error && (
            <div
              className="flex items-center gap-2 rounded-xl p-4 text-sm"
              style={{
                backgroundColor: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                color: '#fca5a5',
              }}
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* ---------- History settings ---------- */}
          <div>
            <SectionHeader title="History Settings" />

            <Card className="border-0" style={panel}>
              <CardContent className="p-6 space-y-6">
                <div className="flex items-start gap-4">
                  <div
                    className="rounded-xl p-2.5 flex-shrink-0"
                    style={{ backgroundColor: 'rgba(13,148,136,0.15)' }}
                  >
                    <Activity className="h-5 w-5" style={{ color: '#2dd4bf' }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-base font-semibold mb-1" style={{ color: '#ffffff' }}>
                      Keep a history of machine readings
                    </div>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: 'rgba(255,255,255,0.55)' }}
                    >
                      Machines report roughly every {interval} minutes, but only the
                      most recent reading is normally kept. Turn history on for a
                      machine and every reading is saved instead, so you can look
                      back at how it has behaved over time.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { icon: Server, label: 'Machines recording', value: `${settings?.enabled_machine_count ?? 0} of ${settings?.total_machine_count ?? 0}` },
                    { icon: Database, label: 'Readings saved', value: totalSamples.toLocaleString() },
                    { icon: Clock, label: 'Keeping per machine', value: `${(settings?.default_retention_count ?? 35040).toLocaleString()} readings` },
                  ].map(({ icon: Icon, label, value }) => (
                    <div
                      key={label}
                      className="rounded-xl p-4"
                      style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className="h-3.5 w-3.5" style={{ color: 'rgba(255,255,255,0.4)' }} />
                        <span
                          className="text-xs font-semibold uppercase tracking-wider"
                          style={{ color: 'rgba(255,255,255,0.45)' }}
                        >
                          {label}
                        </span>
                      </div>
                      <div className="text-2xl font-bold" style={{ color: '#ffffff' }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ---------- Default fields ---------- */}
          <div>
            <SectionHeader title="Default Fields" />
            <Card className="border-0" style={panel}>
              <CardContent className="p-6">
                <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                  These are the values saved from every reading. Anything else in
                  a machine's report — container lists, running processes, failed
                  task lists — is left out, so the history stays small.
                </p>
                <div className="flex flex-wrap gap-2">
                  {(settings?.default_metrics ?? []).map((m) => (
                    <div
                      key={m.name}
                      title={m.field_path}
                      className="flex items-center gap-2 rounded-lg px-3 py-2"
                      style={{
                        backgroundColor: 'rgba(13,148,136,0.1)',
                        border: '1px solid rgba(13,148,136,0.25)',
                      }}
                    >
                      <Check className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#2dd4bf' }} />
                      <span className="text-sm" style={{ color: '#e2e8f0' }}>
                        {prettyMetric(m.name)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ---------- Machines recording history ---------- */}
          <div>
            <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
              <div className="flex-1 min-w-0">
                <SectionHeader title="Machines Recording History" />
                <p className="text-xs -mt-3 mb-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Select a machine to see how its readings have changed over time.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap mb-5">
                {enabled.length > 0 && (
                  <div className="relative" style={{ width: '14rem' }}>
                    <Search
                      className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2"
                      style={{ color: 'rgba(255,255,255,0.4)' }}
                    />
                    <Input
                      placeholder="Search machines..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="h-9 pl-9 text-sm rounded-xl"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: '#e2e8f0',
                      }}
                    />
                  </div>
                )}
                <Button
                  onClick={() => setShowAdd((v) => !v)}
                  className="h-9 rounded-xl text-sm whitespace-nowrap"
                  style={{ backgroundColor: '#0d9488', color: '#ffffff' }}
                >
                  <Plus className="h-4 w-4 mr-1.5 flex-shrink-0" />
                  Turn on for a machine
                </Button>
              </div>
            </div>

            {/* Picker: machines not yet recording */}
            {showAdd && (
              <Card className="border-0 mb-4" style={panel}>
                <CardContent className="p-5">
                  <div className="relative mb-4" style={{ maxWidth: '20rem' }}>
                    <Search
                      className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2"
                      style={{ color: 'rgba(255,255,255,0.4)' }}
                    />
                    <Input
                      placeholder="Find a machine by serial or site..."
                      value={addSearch}
                      onChange={(e) => setAddSearch(e.target.value)}
                      className="h-9 pl-9 text-sm rounded-xl"
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: '#e2e8f0',
                      }}
                    />
                  </div>

                  {candidates.length === 0 ? (
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
                      {allMachines.length === 0
                        ? 'No machines registered yet.'
                        : 'Every activated machine matching your search is already recording. Trial machines do not record history.'}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {candidates.map((m) => (
                        <div
                          key={m.serial_number}
                          className="flex items-center justify-between gap-4 rounded-xl px-4 py-3"
                          style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                        >
                          <div className="min-w-0">
                            <div
                              className="text-sm font-medium truncate"
                              style={{ color: '#e2e8f0' }}
                            >
                              {m.serial_number}
                            </div>
                            <div
                              className="text-xs truncate"
                              style={{ color: 'rgba(255,255,255,0.4)' }}
                            >
                              {m.site_name || 'No site assigned'} · {m.activation_status}
                            </div>
                          </div>
                          <Toggle
                            on={false}
                            busy={saving === m.serial_number}
                            onClick={() => setHistory(m.serial_number, true)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Enabled machines */}
            {filteredEnabled.length === 0 ? (
              <Card className="border-0" style={panel}>
                <CardContent className="p-10 text-center">
                  <Database
                    className="h-8 w-8 mx-auto mb-3"
                    style={{ color: 'rgba(255,255,255,0.2)' }}
                  />
                  <div className="text-sm font-medium mb-1" style={{ color: '#e2e8f0' }}>
                    {enabled.length === 0
                      ? 'No machine is recording history yet'
                      : 'No machines match your search'}
                  </div>
                  {enabled.length === 0 && (
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
                      Use “Turn on for a machine” above to start keeping readings.
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 overflow-hidden" style={panel}>
                {/* Wide table scrolls inside its own container so the page never does. */}
                <CardContent className="p-0 overflow-x-auto">
                  {/* header row */}
                  <div
                    className="hidden md:grid px-5 py-3 text-xs font-semibold uppercase tracking-wider min-w-[920px]"
                    style={{
                      gridTemplateColumns: ROW_COLUMNS,
                      gap: '1rem',
                      color: 'rgba(255,255,255,0.4)',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <div>Machine</div>
                    <div>Site</div>
                    <div>CPU (24h)</div>
                    <div>Readings</div>
                    <div>Covering</div>
                    <div>Keeping</div>
                    <div>Last reading</div>
                    <div className="text-right">History</div>
                  </div>

                  {filteredEnabled.map((m: HistoryMachine, i) => (
                    <div
                      key={m.serial_number}
                      onClick={() => setOpenMachine(m)}
                      className="px-5 py-4 md:grid md:items-center md:min-w-[920px] cursor-pointer transition-colors hover:bg-white/[0.03]"
                      style={{
                        gridTemplateColumns: ROW_COLUMNS,
                        gap: '1rem',
                        borderBottom:
                          i < filteredEnabled.length - 1
                            ? '1px solid rgba(255,255,255,0.04)'
                            : undefined,
                      }}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate" style={{ color: '#e2e8f0' }}>
                          {m.serial_number}
                        </div>
                        {m.custom_metrics.length > 0 && (
                          <div className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                            Custom fields: {m.custom_metrics.map(prettyMetric).join(', ')}
                          </div>
                        )}
                      </div>

                      <div className="text-sm truncate" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        <span className="md:hidden" style={{ color: 'rgba(255,255,255,0.4)' }}>Site: </span>
                        {m.site_name || '—'}
                      </div>

                      <div className="py-1">
                        <Sparkline
                          values={trends[m.serial_number] || []}
                          width={120}
                          height={26}
                          percent
                        />
                      </div>

                      <div className="text-sm font-semibold" style={{ color: '#ffffff' }}>
                        <span className="md:hidden font-normal" style={{ color: 'rgba(255,255,255,0.4)' }}>Readings: </span>
                        {m.sample_count.toLocaleString()}
                      </div>

                      <div className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        <span className="md:hidden" style={{ color: 'rgba(255,255,255,0.4)' }}>Covering: </span>
                        {coverage(m.sample_count, interval)}
                      </div>

                      <div className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        <span className="md:hidden" style={{ color: 'rgba(255,255,255,0.4)' }}>Keeping: </span>
                        {m.history_retention_count.toLocaleString()}
                      </div>

                      <div className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                        <span className="md:hidden" style={{ color: 'rgba(255,255,255,0.4)' }}>Last: </span>
                        {relativeTime(m.latest_sample)}
                      </div>

                      <div
                        className="flex md:justify-end mt-2 md:mt-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Toggle
                          on={m.history_enabled}
                          busy={saving === m.serial_number}
                          onClick={() => setHistory(m.serial_number, false)}
                        />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {openMachine && (
        <MachineHistoryPanel
          serialNumber={openMachine.serial_number}
          siteName={openMachine.site_name}
          onClose={() => setOpenMachine(null)}
        />
      )}
    </div>
  );
};

export default AnalyticsPage;
